// src/utils/dateUtils.js
// Date utility functions for consistent timezone handling

import { format } from 'date-fns'

/**
 * Format UTC database date to display format without timezone conversion
 * This prevents the common issue where UTC dates are shifted by timezone offset
 * @param {string|Date} dateString - The date string from database (usually UTC)
 * @param {string} formatString - The format string (default: 'MMM dd, yyyy HH:mm')
 * @returns {string} Formatted date string
 */
export const formatUTCDate = (dateString, formatString = 'MMM dd, yyyy HH:mm') => {
  if (!dateString) return ''
  
  try {
    const date = new Date(dateString)
    
    // Use UTC methods to extract date components without timezone conversion
    const year = date.getUTCFullYear()
    const month = date.getUTCMonth()
    const day = date.getUTCDate()
    const hours = date.getUTCHours()
    const minutes = date.getUTCMinutes()
    const seconds = date.getUTCSeconds()
    
    // Create a new date object with UTC values but in local timezone context
    const utcDate = new Date(year, month, day, hours, minutes, seconds)
    
    return format(utcDate, formatString)
  } catch (error) {
    console.error('Error formatting UTC date:', error)
    return dateString
  }
}

/**
 * Format UTC database date to locale string format without timezone conversion
 * @param {string|Date} dateString - The date string from database (usually UTC)
 * @param {Object} options - Locale string options
 * @returns {string} Formatted date string
 */
export const formatUTCDateLocale = (dateString, options = {
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true
}) => {
  if (!dateString) return ''
  
  try {
    const date = new Date(dateString)
    
    // Use UTC methods to extract date components without timezone conversion
    const year = date.getUTCFullYear()
    const month = date.getUTCMonth()
    const day = date.getUTCDate()
    const hours = date.getUTCHours()
    const minutes = date.getUTCMinutes()
    const seconds = date.getUTCSeconds()
    
    // Create a new date object with UTC values but in local timezone context
    const utcDate = new Date(year, month, day, hours, minutes, seconds)
    
    return utcDate.toLocaleString('en-US', options)
  } catch (error) {
    console.error('Error formatting UTC date locale:', error)
    return dateString
  }
}

/**
 * Get today's date string in YYYY-MM-DD format for API queries
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayDateString = () => {
  return format(new Date(), 'yyyy-MM-dd')
}

export const getLocalStartOfDayISO = (dateStr) => {
  if (!dateStr) return undefined;
  if (dateStr.includes('T')) return dateStr; // Already formatted
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  return new Date(year, month, day, 0, 0, 0, 0).toISOString();
};

export const getLocalEndOfDayISO = (dateStr) => {
  if (!dateStr) return undefined;
  if (dateStr.includes('T')) return dateStr; // Already formatted
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  return new Date(year, month, day, 23, 59, 59, 999).toISOString();
};

export default {
  formatUTCDate,
  formatUTCDateLocale,
  getTodayDateString,
  getLocalStartOfDayISO,
  getLocalEndOfDayISO
}
